/**
 * MessageTimestamp — Intelligent relative timestamps for chat messages
 *
 * Display rules:
 *   - < 1 min ago   => "now"
 *   - < 60 min ago  => "3m"
 *   - < 24h ago     => "2h"
 *   - Yesterday     => "Yesterday 14:30" / "אתמול 14:30"
 *   - This week     => "Mon 14:30" / "יום ב׳ 14:30"
 *   - Older         => "12 Jan 14:30" / "12 בינו׳ 14:30"
 *
 * Re-renders every 60 seconds via useEffect interval.
 * Supports Hebrew locale via i18n.language.
 */

import { memo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface MessageTimestampProps {
  /** ISO 8601 date string or epoch ms */
  date: string | number;
  className?: string;
}

/** Short Hebrew day names indexed by getDay() (0=Sun) */
const HE_DAYS = ['יום א׳', 'יום ב׳', 'יום ג׳', 'יום ד׳', 'יום ה׳', 'יום ו׳', 'שבת'] as const;
/** Short English day names indexed by getDay() */
const EN_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Short Hebrew month names (1-indexed internally, but array is 0-indexed) */
const HE_MONTHS = [
  'בינו׳', 'בפבר׳', 'במרץ', 'באפר׳', 'במאי', 'ביוני',
  'ביולי', 'באוג׳', 'בספט׳', 'באוק׳', 'בנוב׳', 'בדצמ׳',
] as const;
const EN_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatHHMM(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** Check whether two dates fall on the same calendar day */
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Check whether `target` is yesterday relative to `now` */
function isYesterday(target: Date, now: Date): boolean {
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameDay(target, yesterday);
}

/** Check whether `target` is within the last 7 calendar days (but not today/yesterday) */
function isThisWeek(target: Date, now: Date): boolean {
  const sixDaysAgo = new Date(now);
  sixDaysAgo.setDate(sixDaysAgo.getDate() - 6);
  sixDaysAgo.setHours(0, 0, 0, 0);
  return target >= sixDaysAgo;
}

function formatRelative(dateInput: string | number, lang: string): string {
  const date = new Date(dateInput);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const isHe = lang === 'he';

  // Future or just now (< 1 min)
  if (diffMin < 1) {
    return isHe ? 'עכשיו' : 'now';
  }

  // Minutes (< 60 min)
  if (diffMin < 60) {
    return isHe ? `${diffMin}ד׳` : `${diffMin}m`;
  }

  // Hours — same calendar day
  if (isSameDay(date, now) && diffHr < 24) {
    return isHe ? `${diffHr}ש׳` : `${diffHr}h`;
  }

  const time = formatHHMM(date);

  // Yesterday
  if (isYesterday(date, now)) {
    const label = isHe ? 'אתמול' : 'Yesterday';
    return `${label} ${time}`;
  }

  // This week (within last 7 days)
  if (isThisWeek(date, now)) {
    const dayName = isHe ? HE_DAYS[date.getDay()] : EN_DAYS[date.getDay()];
    return `${dayName} ${time}`;
  }

  // Older — "12 Jan 14:30"
  const day = date.getDate();
  const month = isHe ? HE_MONTHS[date.getMonth()] : EN_MONTHS[date.getMonth()];
  return isHe ? `${day} ${month} ${time}` : `${day} ${month} ${time}`;
}

export const MessageTimestamp = memo(function MessageTimestamp({
  date,
  className = '',
}: MessageTimestampProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const compute = useCallback(() => formatRelative(date, lang), [date, lang]);
  const [display, setDisplay] = useState(compute);

  useEffect(() => {
    // Recompute immediately when date/lang changes
    setDisplay(formatRelative(date, lang));

    // Update every 60 seconds
    const interval = setInterval(() => {
      setDisplay(formatRelative(date, lang));
    }, 60_000);

    return () => clearInterval(interval);
  }, [date, lang]);

  return (
    <time
      dateTime={new Date(date).toISOString()}
      className={`text-[10px] tabular-nums text-neutral-800/50 ${className}`}
      dir="ltr"
      title={new Date(date).toLocaleString(lang === 'he' ? 'he-IL' : 'en-US')}
    >
      {display}
    </time>
  );
});

export default MessageTimestamp;
